import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { DEFAULT_SETTINGS, KPIS, RULES, type KpiGroup, type KpiId, type RuleId, type Settings } from '@serb/shared';
import { BOTTOM_NAV_HEIGHT } from '../../components/BottomNav';
import { DataTable } from '../../components/DataTable';
import { Panel } from '../../components/Panel';
import { StatusChip } from '../../components/StatusChip';
import { useAction } from '../../lib/useAction';
import { setSettings, useSettings } from '../../lib/settingsStore';
import { NumberInput } from './NumberInput';
import { RuleBookPanels } from './RuleBookPanels';
import { settingsApi } from './api';

const GROUPS: KpiGroup[] = ['Financial', 'Agents', 'Carriers'];

/** The numbers are kept as text while typing, so a half-typed "0." is not lost */
type Draft = { fx: Record<'USD' | 'EUR', string>; kpis: Record<KpiId, string>; rules: Record<RuleId, string> };
const toDraft = (s: Settings): Draft => ({
  fx: { USD: String(s.fx.USD), EUR: String(s.fx.EUR) },
  kpis: Object.fromEntries(KPIS.map((k) => [k.id, String(s.kpis[k.id])])) as Record<KpiId, string>,
  rules: Object.fromEntries(RULES.map((r) => [r.id, String(s.rules[r.id])])) as Record<RuleId, string>,
});
const fromDraft = (d: Draft, saved: Settings): Settings => ({
  ...saved, // the notification choices are kept as they are
  fx: { USD: Number(d.fx.USD), EUR: Number(d.fx.EUR) },
  kpis: Object.fromEntries(KPIS.map((k) => [k.id, Number(d.kpis[k.id])])) as Record<KpiId, number>,
  rules: Object.fromEntries(RULES.map((r) => [r.id, Number(d.rules[r.id])])) as Record<RuleId, number>,
});

export type SettingsTab = 'General' | 'KPI Targets' | 'Rule Book';

/** Exchange rates, the KPI sheet and the rule book: one draft, one Save button, shown a tab at a time. Every dashboard reads these. */
export function SettingsForm({ tab }: { tab: SettingsTab }) {
  const saved = useSettings();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(saved));
  const save = useAction(settingsApi.save, {
    success: 'Settings saved. Every dashboard now uses them.',
    onDone: (s) => {
      // customers, agents and carriers are marked Active or Inactive by the server, using the rule book
      if (JSON.stringify(s.rules) !== JSON.stringify(saved.rules)) {
        ['customers', 'agents', 'carriers'].forEach((key) => void qc.invalidateQueries({ queryKey: [key] }));
      }
      setSettings(s);
    },
  });
  const changed = JSON.stringify(draft) !== JSON.stringify(toDraft(saved));
  const setFx = (c: 'USD' | 'EUR', v: string) => setDraft((d) => ({ ...d, fx: { ...d.fx, [c]: v } }));
  const setKpi = (id: KpiId, v: string) => setDraft((d) => ({ ...d, kpis: { ...d.kpis, [id]: v } }));
  const setRule = (id: RuleId, v: string) => setDraft((d) => ({ ...d, rules: { ...d.rules, [id]: v } }));

  return (
    <Stack spacing={2}>
      {tab === 'General' && (
        <Panel
          title="Exchange rates"
          subtitle="How many JOD one unit of each currency is worth. Every money figure in the app is worked out with these."
        >
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              maxWidth: 560,
            }}
          >
            {(['USD', 'EUR'] as const).map((c) => (
              <TextField
                key={c}
                label={`1 ${c} =`}
                value={draft.fx[c]}
                onChange={(e) => setFx(c, e.target.value)}
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  input: { endAdornment: <InputAdornment position="end">JOD</InputAdornment> },
                }}
              />
            ))}
          </Box>
        </Panel>
      )}

      {tab === 'KPI Targets' &&
        GROUPS.map((group) => (
          <Panel
            key={group}
            title={`KPI sheet: ${group}`}
            subtitle={
              group === 'Financial'
                ? 'The acceptable rate for each KPI. A dashboard shows the actual number next to it and says whether it is met.'
                : undefined
            }
          >
            <DataTable<(typeof KPIS)[number]>
              rows={KPIS.filter((k) => k.group === group)}
              rowKey={(k) => k.id}
              columns={[
                {
                  header: 'KPI',
                  primary: true,
                  render: (k) => (
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{k.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {k.help}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  header: 'Good when',
                  nowrap: true,
                  render: (k) => <StatusChip label={k.better === 'higher' ? 'At least' : 'At most'} tone="neutral" />,
                },
                {
                  header: 'Acceptable rate',
                  nowrap: true,
                  render: (k) => (
                    <NumberInput
                      value={draft.kpis[k.id]}
                      onChange={(v) => setKpi(k.id, v)}
                      label={`${k.label}: acceptable rate`}
                      before={k.better === 'higher' ? '≥' : '≤'}
                      after={k.unit}
                    />
                  ),
                },
                { header: 'Shown on', render: (k) => k.shownOn },
              ]}
            />
          </Panel>
        ))}

      {tab === 'Rule Book' && <RuleBookPanels draft={draft.rules} onChange={setRule} />}

      <Paper
        variant="outlined"
        sx={{
          position: 'sticky',
          bottom: { xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`, md: 0 },
          zIndex: 2,
          p: 1.25,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" color={changed ? 'warning.main' : 'text.secondary'} sx={{ fontWeight: 600 }}>
          {changed ? 'You have changes that are not saved yet.' : 'All changes are saved.'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="inherit" onClick={() => setDraft(toDraft(DEFAULT_SETTINGS))}>
            Reset to defaults
          </Button>
          <Button
            variant="contained"
            disabled={!changed || save.busy}
            onClick={() => save.run(fromDraft(draft, saved))}
          >
            Save settings
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
